import { createSelector } from 'reselect'
import { isSpoofed } from 'selectors/isSpoofed'
import config from 'constants/config'
import {
  findPostPurchaseCheckout,
  isLEHotelItem,
} from 'lib/checkout/checkoutUtils'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import {
  AGENT_HUB_PAYMENT_MODE,
  LPC_50_DEFAULT_SUPPLIER_PAYMENT_DAYS,
  SPOOF_HOTEL_20_PERCENT_PAYMENT_SCHEDULE_TEMPLATE_ID,
} from 'constants/payment'
import { subDays } from 'lib/datetime/dateUtils'
import getAllItemsDiscountTotals, { getAllItemsVoucherTotals } from '../getAllItemsDiscountTotals'
import { checkoutWithMemberPrice } from 'checkout/selectors/view/luxPlusSubscription'
import { getInsuranceTotals } from '../insurance'
import { getAccommodationBreakdownView } from 'checkout/selectors/view/accommodation'
import getAllItemViews from 'checkout/selectors/view/getAllItemViews'
import getExperienceItemsView from 'checkout/selectors/view/itemViews/getExperienceItemsView'
import getLoyaltyLoungePassItemViews from 'checkout/selectors/view/itemViews/getLoyaltyLoungePassItemViews'
import getLoyaltyTransferItemView from 'checkout/selectors/view/itemViews/getLoyaltyTransferItemView'
import { getAllOffers } from 'selectors/offerSelectors'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import isPaymentScheduleEnabled from './isPaymentScheduleEnabled'
import { isLeHotelPaymentScheduleCartShape } from './isPaymentScheduleAvailable'
import { getCartHasPropertyCollectItem } from '../propertyCollectSelectors'
import { selectIsGuestCheckoutMode } from 'selectors/checkoutSelectors'
import { buildPaymentScheduleItems, calculateGiftCardAmount } from './paymentScheduleItemUtils'

export const isSpoofHotelTwentyPercentPaymentScheduleAvailable = createSelector(
  (state: App.State) => isSpoofed(state),
  (state: App.State) => isPaymentScheduleEnabled(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => selectIsGuestCheckoutMode(state),
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getCartHasPropertyCollectItem(state),
  (
    spoofed,
    paymentScheduleEnabled,
    postPurchase,
    isGuestCheckoutMode,
    isGift,
    cartItems,
    hasPropertyCollectItem,
  ): boolean => {
    if (
      !spoofed ||
      config.APP_ENV === 'production' ||
      !paymentScheduleEnabled ||
      postPurchase ||
      isGuestCheckoutMode ||
      hasPropertyCollectItem
    ) {
      return false
    }

    return isLeHotelPaymentScheduleCartShape(cartItems, isGift)
  },
)

export const getSpoofHotelTwentyPercentPaymentScheduleRequestInfo = createSelector(
  isSpoofHotelTwentyPercentPaymentScheduleAvailable,
  (state: App.State) => getInsuranceTotals(state),
  (state: App.State) => getAllItemsDiscountTotals(state),
  (state: App.State) => getAllItemsVoucherTotals(state),
  (state: App.State) => checkoutWithMemberPrice(state),
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getAllOffers(state),
  (state: App.State) => getAllItemViews(state),
  (state: App.State) => getExperienceItemsView(state),
  (state: App.State) => getAccommodationBreakdownView(state),
  (state: App.State) => state.checkout.commissionData?.itemsCommission,
  (state: App.State) => state.checkout.payment.agentHubPaymentMode,
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => state.checkout.payment.useCredit,
  (state: App.State) => state.checkout.payment.useGiftCard,
  (state: App.State) => state.redeemedGiftCards.cards,
  (state: App.State) => getLoyaltyLoungePassItemViews(state),
  (state: App.State) => getLoyaltyTransferItemView(state),
  (
    isAvailable,
    insuranceTotals,
    discountTotalToCartItemsMap,
    voucherTotalToCartItemsMap,
    checkoutWithMemberPrice,
    cartItems,
    offers,
    itemViews,
    experienceItemViews,
    accommodationBreakdownView,
    itemsCommission,
    agentHubPaymentMode,
    currency,
    isCreditApplied,
    isGiftCardApplied,
    redeemedGiftCards,
    loyaltyLoungePassItemViews,
    loyaltyTransferItemViews,
  ): App.Checkout.PaymentScheduleRequestInfo | null => {
    if (!isAvailable) {
      return null
    }

    const hotelItem = cartItems.find(isLEHotelItem)
    if (!hotelItem || !('checkIn' in hotelItem) || !hotelItem.checkIn) {
      return null
    }

    const hotelItemView = itemViews.data.get(hotelItem.itemId)?.data as App.Checkout.AccommodationItemView | undefined
    if (!hotelItemView?.totals?.price) {
      return null
    }

    const giftCardAmount = calculateGiftCardAmount(redeemedGiftCards)
    const shouldUseAgentHubCommission = isAgentHubEnabled && agentHubPaymentMode === AGENT_HUB_PAYMENT_MODE.PAY_NET_AMOUNT
    const startDate = hotelItem.checkIn
    const dueDate = subDays(new Date(startDate), LPC_50_DEFAULT_SUPPLIER_PAYMENT_DAYS)

    const items = buildPaymentScheduleItems(cartItems, {
      offers,
      discountTotalToCartItemsMap,
      checkoutWithMemberPrice,
      insuranceTotals,
      accommodationBreakdownView,
      itemViews,
      experienceItemViews,
      loyaltyLoungePassItemViews,
      loyaltyTransferItemViews,
      agentHubCommission: {
        shouldUse: shouldUseAgentHubCommission,
        itemsCommission,
        voucherTotalToCartItemsMap,
      },
    })

    if (!items) {
      return null
    }

    return {
      templateId: SPOOF_HOTEL_20_PERCENT_PAYMENT_SCHEDULE_TEMPLATE_ID,
      bookingInfo: {
        bookingDate: moment().format(ISO_DATE_FORMAT),
        checkinDate: startDate,
        supplierDueDate: moment(dueDate).format(ISO_DATE_FORMAT),
        isCreditApplied,
        isGiftCardApplied,
        giftCardAmount,
        isPaymentScheduleRebooking: false,
        currency,
        items,
      },
    }
  },
)
